import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, authedUrl, fmtMoney, fmtCur, fmtNum } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, TrendingUp, TrendingDown, Wallet, Download, FileText, Plus, Pencil, Trash2, Layers, ChevronDown, ChevronRight } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";

const PIE_COLORS = ["#10B981", "#F59E0B", "#0EA5E9", "#F43F5E", "#8B5CF6", "#22D3EE", "#EC4899", "#84CC16", "#EAB308", "#A855F7"];
const ALL = "__all__";

const now = new Date();
const monthOpts = () => {
  const out = [];
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  for (let i = 0; i < 12; i++) {
    const d = new Date(start.getFullYear(), start.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    out.push({ value: `${y}-${String(m).padStart(2, "0")}`, label: d.toLocaleString("en-US", { month: "long", year: "numeric" }), year: y, month: m });
  }
  return out;
};

export default function ClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAcc, setSelectedAcc] = useState(ALL);
  const [holdings, setHoldings] = useState(null);
  const [txns, setTxns] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const opts = useMemo(() => monthOpts(), []);
  const [stmtMonth, setStmtMonth] = useState(opts[0].value);
  const [addOpen, setAddOpen] = useState(false);
  const [editAcc, setEditAcc] = useState(null);
  const [accForm, setAccForm] = useState({ name: "", cds_number: "", notes: "" });
  const [txnEditOpen, setTxnEditOpen] = useState(false);
  const [editTxn, setEditTxn] = useState(null);
  const [txnForm, setTxnForm] = useState(null);
  const [expandedSym, setExpandedSym] = useState({}); // symbol -> bool

  const openTxnEdit = (t) => {
    setEditTxn(t);
    setTxnForm({
      account_id: t.account_id,
      symbol: t.symbol,
      trade_type: t.trade_type,
      quantity: String(t.quantity),
      price: String(t.price),
      commission: String(t.commission || 0),
      trade_date: t.trade_date,
      notes: t.notes || "",
    });
    setTxnEditOpen(true);
  };

  const saveTxn = async () => {
    if (!txnForm) return;
    const qty = parseFloat(txnForm.quantity);
    const price = parseFloat(txnForm.price);
    if (!qty || qty <= 0) return toast.error("Invalid quantity");
    if (isNaN(price) || price < 0) return toast.error("Invalid price");
    try {
      await api.put(`/transactions/${editTxn.id}`, {
        account_id: txnForm.account_id,
        symbol: txnForm.symbol,
        trade_type: txnForm.trade_type,
        quantity: qty,
        price,
        commission: parseFloat(txnForm.commission || 0),
        trade_date: txnForm.trade_date,
        notes: txnForm.notes,
      });
      toast.success("Transaction updated");
      setTxnEditOpen(false);
      loadForAccount();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const deleteTxn = async (t) => {
    try {
      await api.delete(`/transactions/${t.id}`);
      toast.success("Transaction deleted");
      loadForAccount();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const loadAll = async () => {
    const [c, a] = await Promise.all([
      api.get(`/clients/${id}`),
      api.get(`/clients/${id}/accounts`),
    ]);
    setClient(c.data);
    setAccounts(a.data);
  };

  const loadForAccount = async () => {
    const q = selectedAcc === ALL ? "" : `?account_id=${selectedAcc}`;
    const [h, t, p] = await Promise.all([
      api.get(`/clients/${id}/holdings${q}`),
      api.get(`/transactions?client_id=${id}${selectedAcc !== ALL ? `&account_id=${selectedAcc}` : ""}`),
      api.get(`/clients/${id}/pnl-timeline${q}`),
    ]);
    setHoldings(h.data);
    setTxns(t.data);
    setTimeline(p.data);
  };

  useEffect(() => { loadAll(); }, [id]);
  useEffect(() => { if (client) loadForAccount(); /* eslint-disable-next-line */ }, [selectedAcc, client]);

  const openAdd = () => { setEditAcc(null); setAccForm({ name: "", cds_number: "", notes: "" }); setAddOpen(true); };
  const openEdit = (a) => { setEditAcc(a); setAccForm({ name: a.name, cds_number: a.cds_number || "", notes: a.notes || "" }); setAddOpen(true); };

  const submitAcc = async () => {
    if (!accForm.name.trim()) return toast.error("Account name required");
    try {
      if (editAcc) {
        await api.put(`/accounts/${editAcc.id}`, accForm);
        toast.success("Account renamed");
      } else {
        await api.post(`/clients/${id}/accounts`, accForm);
        toast.success("Account added");
      }
      setAddOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const removeAcc = async (aid) => {
    try {
      await api.delete(`/accounts/${aid}`);
      toast.success("Account deleted");
      if (selectedAcc === aid) setSelectedAcc(ALL);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (!client || !holdings) return <div className="text-slate-500">Loading...</div>;
  const c = client;
  const t = holdings.totals;

  const pieData = holdings.holdings.map(h => ({ name: h.symbol, value: h.current_value }));
  const selectedOpt = opts.find(o => o.value === stmtMonth) || opts[0];
  const pdfUrl = authedUrl(`/clients/${id}/statement.pdf?year=${selectedOpt.year}&month=${selectedOpt.month}${selectedAcc !== ALL ? `&account_id=${selectedAcc}` : ""}`);
  const csvUrl = authedUrl(`/clients/${id}/holdings.csv${selectedAcc !== ALL ? `?account_id=${selectedAcc}` : ""}`);
  const activeAccountName = selectedAcc === ALL ? "All Accounts" : (accounts.find(a => a.id === selectedAcc)?.name || "—");

  return (
    <div className="space-y-6">
      <Link to="/clients" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400"><ArrowLeft size={14} /> Back to Clients</Link>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-caps">Client Portfolio</div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl mt-2 tracking-tight" data-testid="client-detail-name">{c.name}</h1>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400 mt-3">
            {c.phone && <span><span className="label-caps mr-2">Phone</span>{c.phone}</span>}
            {c.email && <span><span className="label-caps mr-2">Email</span>{c.email}</span>}
          </div>
          {(c.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {c.tags.map(tag => <span key={tag} className="px-2 py-0.5 rounded-full text-[10px] border bg-slate-800/50 border-slate-700 text-slate-300">{tag}</span>)}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={csvUrl} download data-testid="btn-download-client-csv">
            <Button variant="outline" className="border-slate-700 text-slate-800 hover:text-emerald-400">
              <Download size={14} className="mr-2" /> CSV
            </Button>
          </a>
          <div className="flex items-center gap-2">
            <Select value={stmtMonth} onValueChange={setStmtMonth}>
              <SelectTrigger data-testid="select-statement-month" className="bg-slate-900/70 border-slate-800 w-52 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0E1524] border-slate-800 text-white max-h-80">
                {opts.map(o => <SelectItem key={o.value} value={o.value} data-testid={`stmt-month-${o.value}`}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <a href={pdfUrl} target="_blank" rel="noopener" data-testid="btn-download-statement-pdf">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold">
                <FileText size={14} className="mr-2" /> Statement
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Accounts Manager */}
      <section className="card-tactical p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <div className="label-caps flex items-center gap-2"><Layers size={12} /> CDS Accounts</div>
            <h2 className="font-heading font-bold text-xl mt-1">Sub-Accounts ({accounts.length})</h2>
            <p className="text-xs text-slate-500 mt-1">One client can hold trades in multiple CDS accounts. Every view below aggregates or filters by the selected CDS account. All accounts sum up to the client's total.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-52">
              <Select value={selectedAcc} onValueChange={setSelectedAcc}>
                <SelectTrigger data-testid="select-account-filter" className="bg-slate-900/70 border-slate-800 h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0E1524] border-slate-800 text-white">
                  <SelectItem value={ALL} data-testid="account-option-all">All Accounts</SelectItem>
                  {accounts.map(a => <SelectItem key={a.id} value={a.id} data-testid={`account-option-${a.id}`}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button onClick={openAdd} data-testid="btn-add-account" className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold">
                  <Plus size={14} className="mr-1" /> Add CDS Account
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0E1524] border-slate-800 text-white">
                <DialogHeader><DialogTitle className="font-heading">{editAcc ? "Edit CDS Account" : "New CDS Account"}</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-2">
                  <div>
                    <Label className="label-caps !text-slate-400">Label *</Label>
                    <Input value={accForm.name} onChange={e => setAccForm({ ...accForm, name: e.target.value })} placeholder="e.g. Margin, NRI, Cash" data-testid="form-account-name-input" className="mt-1.5" />
                  </div>
                  <div>
                    <Label className="label-caps !text-slate-400">CDS Account Number</Label>
                    <Input value={accForm.cds_number} onChange={e => setAccForm({ ...accForm, cds_number: e.target.value })} placeholder="e.g. 088-123-456789" data-testid="form-account-cds-input" className="mt-1.5 font-mono-tabular" />
                  </div>
                  <div>
                    <Label className="label-caps !text-slate-400">Notes</Label>
                    <Input value={accForm.notes} onChange={e => setAccForm({ ...accForm, notes: e.target.value })} data-testid="form-account-notes-input" className="mt-1.5" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAddOpen(false)} className="border-slate-700 text-slate-800">Cancel</Button>
                  <Button onClick={submitAcc} data-testid="form-account-submit-button" className="bg-emerald-500 hover:bg-emerald-600 text-slate-950">{editAcc ? "Save" : "Create"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="list-accounts">
          {accounts.map(a => {
            const active = selectedAcc === a.id;
            return (
              <div key={a.id} className={`flex items-center gap-1.5 pl-3 pr-1 py-1.5 rounded-full border ${active ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300" : "bg-slate-900/60 border-slate-800 text-slate-300"}`} data-testid={`account-chip-${a.id}`}>
                <button type="button" onClick={() => setSelectedAcc(active ? ALL : a.id)} className="text-sm font-medium flex items-center gap-2">
                  <span>{a.name}</span>
                  {a.cds_number && <span className="text-[10px] font-mono-tabular text-slate-400">CDS · {a.cds_number}</span>}
                </button>
                <button type="button" onClick={() => openEdit(a)} data-testid={`btn-edit-account-${a.id}`} className="p-1 rounded hover:bg-slate-800/60 text-slate-400"><Pencil size={11} /></button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button type="button" data-testid={`btn-delete-account-${a.id}`} className="p-1 rounded hover:bg-slate-800/60 text-rose-400"><Trash2 size={11} /></button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#0E1524] border-slate-800 text-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete CDS account "{a.name}"?</AlertDialogTitle>
                      <AlertDialogDescription className="text-slate-400">
                        Accounts with existing transactions cannot be deleted. This client must always have at least one account.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-slate-800 border-slate-700">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => removeAcc(a.id)} className="bg-rose-500 hover:bg-rose-600">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            );
          })}
        </div>
      </section>

      <div className="flex items-center gap-2 text-sm text-slate-400">
        <span className="label-caps">Viewing</span>
        <span className="px-2 py-0.5 rounded bg-slate-800/70 text-emerald-300 font-semibold" data-testid="active-account-label">{activeAccountName}</span>
      </div>

      <section className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Metric label="Portfolio Value" value={fmtMoney(t.current_value)} icon={Wallet} />
        <Metric label="Cost Basis" value={fmtMoney(t.cost_basis)} icon={Wallet} />
        <Metric label="Unrealized P&L" value={fmtMoney(t.unrealized_pnl)} icon={t.unrealized_pnl >= 0 ? TrendingUp : TrendingDown} tone={t.unrealized_pnl >= 0 ? "up" : "down"} />
        <Metric label="Realized P&L" value={fmtMoney(t.realized_pnl)} icon={t.realized_pnl >= 0 ? TrendingUp : TrendingDown} tone={t.realized_pnl >= 0 ? "up" : "down"} />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="card-tactical p-6 lg:col-span-5">
          <div className="label-caps">Allocation</div>
          <h2 className="font-heading font-bold text-xl mt-1 mb-4">Portfolio Mix</h2>
          {pieData.length === 0 ? (
            <div className="py-12 text-center text-slate-500 border border-dashed border-slate-800 rounded-lg">No holdings to chart.</div>
          ) : (
            <div className="h-72" data-testid="chart-allocation-pie">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2} stroke="#0E1524">
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0E1524", border: "1px solid #1E2A40", borderRadius: 8, color: "#F1F5F9" }} formatter={(v) => fmtMoney(v)} />
                  <Legend wrapperStyle={{ fontSize: 12, color: "#94A3B8" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        <div className="card-tactical p-6 lg:col-span-7">
          <div className="label-caps">Performance</div>
          <h2 className="font-heading font-bold text-xl mt-1 mb-4">Cumulative Realized P&L</h2>
          {timeline.length === 0 ? (
            <div className="py-12 text-center text-slate-500 border border-dashed border-slate-800 rounded-lg">Realized P&L will appear after your first SELL trade.</div>
          ) : (
            <div className="h-72" data-testid="chart-pnl-trend">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeline} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E2A40" />
                  <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
                  <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `RM ${(v / 1000).toFixed(1)}k`} />
                  <Tooltip contentStyle={{ background: "#0E1524", border: "1px solid #1E2A40", borderRadius: 8, color: "#F1F5F9" }} formatter={(v) => fmtMoney(v)} />
                  <Line type="monotone" dataKey="cumulative_pnl" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3, fill: "#10B981" }} activeDot={{ r: 5 }} name="Cumulative P&L" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>

      <section className="card-tactical p-6">
        <h2 className="font-heading font-bold text-xl mb-4">Current Holdings</h2>
        {holdings.holdings.length === 0 ? (
          <div className="py-10 text-center text-slate-500 border border-dashed border-slate-800 rounded-lg">No active holdings.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tactical" data-testid="table-client-holdings">
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th>Symbol</th>
                  <th className="!text-right">Qty</th>
                  <th className="!text-right">Avg Buy</th>
                  <th className="!text-right">Market Price</th>
                  <th className="!text-right">Value</th>
                  <th className="!text-right">Unrealized P&L</th>
                </tr>
              </thead>
              <tbody>
                {holdings.holdings.map(h => {
                  const byAcc = h.by_account || [];
                  const hasBreakdown = selectedAcc === ALL && byAcc.length > 0;
                  const isOpen = !!expandedSym[h.symbol];
                  return (
                    <React.Fragment key={h.symbol}>
                      <tr
                        data-testid={`holding-row-${h.symbol}`}
                        className={hasBreakdown ? "cursor-pointer hover:bg-slate-800/40" : ""}
                        onClick={() => { if (hasBreakdown) setExpandedSym({ ...expandedSym, [h.symbol]: !isOpen }); }}
                      >
                        <td>
                          {hasBreakdown ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setExpandedSym({ ...expandedSym, [h.symbol]: !isOpen }); }}
                              data-testid={`toggle-holding-${h.symbol}`}
                              className="p-1 rounded hover:bg-slate-800/60 text-slate-400"
                            >
                              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                          ) : null}
                        </td>
                        <td className="font-mono-tabular font-bold text-emerald-400">
                          {h.symbol} <span className="text-[9px] font-mono-tabular text-sky-300 ml-1">{h.currency}</span>
                          {hasBreakdown && (
                            <span className="ml-2 text-[10px] text-slate-500">· {byAcc.length} account{byAcc.length > 1 ? "s" : ""}</span>
                          )}
                        </td>
                        <td className="!text-right font-mono-tabular">{fmtNum(h.quantity, 2)}</td>
                        <td className="!text-right font-mono-tabular">{fmtCur(h.avg_buy_price, h.currency, 2)}</td>
                        <td className="!text-right font-mono-tabular">{fmtCur(h.market_price, h.currency, 2)}</td>
                        <td className="!text-right font-mono-tabular">{fmtCur(h.current_value, h.currency)}</td>
                        <td className={`!text-right font-mono-tabular ${h.unrealized_pnl >= 0 ? "ticker-up" : "ticker-down"}`}>{fmtCur(h.unrealized_pnl, h.currency)}</td>
                      </tr>
                      {hasBreakdown && isOpen && byAcc.map(a => (
                        <tr
                          key={`${h.symbol}-${a.account_id}`}
                          data-testid={`holding-account-row-${h.symbol}-${a.account_id}`}
                          className="bg-slate-900/40 text-slate-300"
                        >
                          <td></td>
                          <td className="pl-8">
                            <span className="text-slate-400 text-xs uppercase tracking-wider">↳ {a.account_name}</span>
                            {a.cds_number && (
                              <span className="text-[10px] font-mono-tabular text-slate-500 ml-2">CDS · {a.cds_number}</span>
                            )}
                          </td>
                          <td className="!text-right font-mono-tabular">{fmtNum(a.quantity, 2)}</td>
                          <td className="!text-right font-mono-tabular">{fmtCur(a.avg_buy_price, h.currency, 2)}</td>
                          <td className="!text-right font-mono-tabular text-slate-500">—</td>
                          <td className="!text-right font-mono-tabular">{fmtCur(a.current_value, h.currency)}</td>
                          <td className={`!text-right font-mono-tabular ${a.unrealized_pnl >= 0 ? "ticker-up" : "ticker-down"}`}>{fmtCur(a.unrealized_pnl, h.currency)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card-tactical p-6">
        <h2 className="font-heading font-bold text-xl mb-4">Trade History</h2>
        {txns.length === 0 ? (
          <div className="py-10 text-center text-slate-500 border border-dashed border-slate-800 rounded-lg">No transactions.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tactical" data-testid="table-client-transactions">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Account</th>
                  <th>Type</th>
                  <th>Symbol</th>
                  <th className="!text-right">Qty</th>
                  <th className="!text-right">Price</th>
                  <th className="!text-right">Commission</th>
                  <th className="!text-right">Total</th>
                  <th>Entered By</th>
                  <th className="!text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {txns.map(t => (
                  <tr key={t.id} data-testid={`txn-row-${t.id}`}>
                    <td className="text-slate-300">{t.trade_date}</td>
                    <td className="text-slate-300">{t.account_name || "—"}</td>
                    <td><span className={`px-2 py-0.5 rounded text-[10px] font-bold ${t.trade_type === "BUY" ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>{t.trade_type}</span></td>
                    <td className="font-mono-tabular font-bold">{t.symbol} <span className="text-[9px] text-sky-300 ml-1">{t.currency || "RM"}</span></td>
                    <td className="!text-right font-mono-tabular">{fmtNum(t.quantity, 2)}</td>
                    <td className="!text-right font-mono-tabular">{fmtCur(t.price, t.currency || "RM")}</td>
                    <td className="!text-right font-mono-tabular text-slate-400">{fmtCur(t.commission, t.currency || "RM")}</td>
                    <td className="!text-right font-mono-tabular">{fmtCur(t.quantity * t.price + (t.trade_type === "BUY" ? t.commission : -t.commission), t.currency || "RM")}</td>
                    <td className="text-xs">
                      <div className="flex flex-col">
                        <span className="text-slate-300 font-semibold">{t.created_by_username || "—"}</span>
                        {t.updated_by_username && (
                          <span className="text-amber-400 text-[10px]" title={t.updated_at}>edited by {t.updated_by_username}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => openTxnEdit(t)} data-testid={`btn-edit-txn-${t.id}`} className="border-slate-700 text-slate-700 hover:text-emerald-400 h-8 w-8 p-0"><Pencil size={12} /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" data-testid={`btn-delete-txn-${t.id}`} className="border-slate-700 text-rose-400 hover:text-rose-300 h-8 w-8 p-0"><Trash2 size={12} /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#0E1524] border-slate-800 text-white">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this transaction?</AlertDialogTitle>
                              <AlertDialogDescription className="text-slate-400">
                                {t.trade_type} {t.quantity} × {t.symbol} on {t.trade_date}. This affects avg cost & P&L for subsequent trades in this scope.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-slate-800 border-slate-700">Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteTxn(t)} className="bg-rose-500 hover:bg-rose-600">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Dialog open={txnEditOpen} onOpenChange={setTxnEditOpen}>
        <DialogContent className="bg-[#0E1524] border-slate-800 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Edit Transaction</DialogTitle></DialogHeader>
          {txnForm && (
            <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto pr-1">
              <div className="flex gap-2">
                <Button onClick={() => setTxnForm({ ...txnForm, trade_type: "BUY" })} data-testid="edit-txn-type-buy" className={`flex-1 h-10 font-bold ${txnForm.trade_type === "BUY" ? "bg-emerald-500 hover:bg-emerald-600 text-slate-950" : "bg-slate-800/60 text-slate-300 border border-slate-700"}`}>BUY</Button>
                <Button onClick={() => setTxnForm({ ...txnForm, trade_type: "SELL" })} data-testid="edit-txn-type-sell" className={`flex-1 h-10 font-bold ${txnForm.trade_type === "SELL" ? "bg-rose-500 hover:bg-rose-600 text-slate-950" : "bg-slate-800/60 text-slate-300 border border-slate-700"}`}>SELL</Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-caps !text-slate-400">CDS Account</Label>
                  <Select value={txnForm.account_id} onValueChange={v => setTxnForm({ ...txnForm, account_id: v })}>
                    <SelectTrigger data-testid="edit-txn-account" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0E1524] border-slate-800 text-white">
                      {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}{a.cds_number ? ` · ${a.cds_number}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-caps !text-slate-400">Trade Date</Label>
                  <Input type="date" value={txnForm.trade_date} onChange={e => setTxnForm({ ...txnForm, trade_date: e.target.value })} data-testid="edit-txn-date" className="mt-1.5" />
                </div>
                <div>
                  <Label className="label-caps !text-slate-400">Symbol</Label>
                  <Input value={txnForm.symbol} onChange={e => setTxnForm({ ...txnForm, symbol: e.target.value.toUpperCase() })} data-testid="edit-txn-symbol" className="mt-1.5 font-mono-tabular uppercase" />
                </div>
                <div>
                  <Label className="label-caps !text-slate-400">Quantity</Label>
                  <Input type="number" step="any" value={txnForm.quantity} onChange={e => setTxnForm({ ...txnForm, quantity: e.target.value })} data-testid="edit-txn-quantity" className="mt-1.5 font-mono-tabular" />
                </div>
                <div>
                  <Label className="label-caps !text-slate-400">Price</Label>
                  <Input type="number" step="any" value={txnForm.price} onChange={e => setTxnForm({ ...txnForm, price: e.target.value })} data-testid="edit-txn-price" className="mt-1.5 font-mono-tabular" />
                </div>
                <div>
                  <Label className="label-caps !text-slate-400">Commission</Label>
                  <Input type="number" step="any" value={txnForm.commission} onChange={e => setTxnForm({ ...txnForm, commission: e.target.value })} data-testid="edit-txn-commission" className="mt-1.5 font-mono-tabular" />
                </div>
              </div>
              <div>
                <Label className="label-caps !text-slate-400">Notes</Label>
                <Input value={txnForm.notes} onChange={e => setTxnForm({ ...txnForm, notes: e.target.value })} data-testid="edit-txn-notes" className="mt-1.5" />
              </div>
              {editTxn?.created_by_username && (
                <div className="text-xs text-slate-500 pt-2 border-t border-slate-800">
                  Original entry by <span className="text-slate-300 font-semibold">{editTxn.created_by_username}</span>{editTxn.updated_by_username && <> · last edited by <span className="text-amber-400">{editTxn.updated_by_username}</span></>}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTxnEditOpen(false)} className="border-slate-700 text-slate-800">Cancel</Button>
            <Button onClick={saveTxn} data-testid="btn-save-txn-edit" className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Metric = ({ label, value, icon: Icon, tone = "default" }) => {
  const color = tone === "up" ? "text-emerald-400" : tone === "down" ? "text-rose-400" : "text-white";
  return (
    <div className="card-tactical p-5">
      <div className="flex items-center justify-between">
        <div className="label-caps">{label}</div>
        <Icon size={16} className="text-emerald-400" />
      </div>
      <div className={`metric-value text-2xl mt-3 ${color}`}>{value}</div>
    </div>
  );
};
